import { ToastProgrammatic as Toast } from 'buefy';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { V1Scheduler } from 'takeorder-groupbuying-scheduler-js';
import { mapActions } from 'vuex';
import useVuelidate from '@vuelidate/core';

import ScheduleList from '@/components/Order/OrderCreate/ScheduleList/ScheduleList.vue';
import ScheduleConfirmModal from '@/components/Order/ScheduleConfirmModal/ScheduleConfirmModal.vue';
import ScheduleModal from '@/components/Order/ScheduleModal/ScheduleModal.vue';
import {
  DELETE_SCHEDULE,
  UPDATE_SCHEDULE,
} from '@/store/orders/constants';
import bus from '@/utils/bus';
import { toCamelCase } from '@/utils/caseConvertor';
import { toClientSchedule, toServerSchedule } from '@/utils/cycleOrder';

const MODAL_WIDTH_LARGE = 722;
const MODAL_WIDTH_SMALL = 448;

export default {
  name: 'ScheduleListModal',
  components: {
    ScheduleList,
    ScheduleConfirmModal,
    ScheduleModal,
  },
  props: {
    loading: {
      default: false,
      type: Boolean,
    },
    order: {
      default: () => ({}),
      type: Object,
    },
    value: {
      default: false,
      type: Boolean,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      departmentIds: [],
      isLoading: false,
      enableSchedule: false,
      modalWidth: MODAL_WIDTH_LARGE,
      openScheduleChangeConfirmModal: false,
      openScheduleCloseConfirmModal: false,
      openScheduleModal: false,
      schedule: null,
      scheduleChange: {},
    };
  },
  computed: {
    hasSchedule() {
      return !isEmpty(this.schedule);
    },
    isConfirmBtnDisable() {
      if (!this.hasSchedule) {
        return false;
      }
      return !this.v$.enableSchedule.$dirty;
    },
    receiveDateStart() {
      const { startTime } = this.sortedReceiveTimes?.[0] || {};
      const start = startTime ? dayjs(startTime) : null;
      if (start?.isAfter(dayjs(), 'day')) {
        return start.toDate();
      }

      const date = start ? dayjs().set('hour', start.hour()).set('minute', start.minute()) : dayjs();
      return date.toDate();
    },
    schedules() {
      if (!this.hasSchedule) {
        return [];
      }

      const {
        type,
        dayInterval,
        weekdayIntervals = [],
        orderStart,
        orderPeriodDay,
        orderPeriodTime = {},
        orderCreateDay,
        orderCreateTime,
        receiveDateEnd,
        groupBuyingReceiveTimes = [],
      } = this.schedule || {};
      const config = {
        type,
        period: dayInterval,
        weekday: [...weekdayIntervals],
        orderStart,
        orderPeriodDay,
        orderPeriodTime: [
          dayjs(orderPeriodTime.start).format('HH:mm'),
          dayjs(orderPeriodTime.end).format('HH:mm'),
        ],
        orderCreateDay,
        orderCreateTime: dayjs(orderCreateTime).format('HH:mm'),
        lastReceiveDate: dayjs(receiveDateEnd).format('YYYY-MM-DD'),
        groupBuyingStartTime: this.receiveDateStart
          ? dayjs(this.receiveDateStart).format() : null,
        groupBuyingReceiveTimes,
      };

      const schedules = V1Scheduler.getAllSchedule(config);
      return schedules;
    },
    sortedReceiveTimes() {
      const { receive_times: receiveTimes = [] } = this.order || {};
      const sourceReceiveTimes = [...toCamelCase(receiveTimes)];
      return sourceReceiveTimes.sort(
        (a, b) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix(),
      );
    },
  },
  created() {
    this.isLoading = this.loading;
  },
  mounted() {
    this.departmentIds = [];
    this.enableSchedule = !!this.hasSchedule;
    this.$nextTick(() => {
      this.modalWidth = this.hasSchedule ? MODAL_WIDTH_LARGE : MODAL_WIDTH_SMALL;
    });
  },
  watch: {
    enableSchedule(val) {
      const { department_ids: orderDepartmentIds = [] } = this.order || {};
      this.departmentIds = orderDepartmentIds || [];
      if (!val && !this.openScheduleModal) {
        this.schedule.departmentIds = orderDepartmentIds || [];
      }
    },
    hasSchedule(val) {
      this.modalWidth = val ? MODAL_WIDTH_LARGE : MODAL_WIDTH_SMALL;
    },
    loading(val) {
      this.isLoading = val;
    },
    value(val) {
      if (val) {
        this.schedule = toClientSchedule(this.order?.schedule || {});
        this.departmentIds = [];
        this.enableSchedule = !!this.hasSchedule;
      }
    },
  },
  methods: {
    ...mapActions('orders', {
      deleteSchedule: DELETE_SCHEDULE,
      updateSchedule: UPDATE_SCHEDULE,
    }),
    close() {
      this.$emit('close');
      this.reset();
    },
    async doDeleteSchedule() {
      try {
        this.isLoading = true;
        const { id } = this.order;

        await this.deleteSchedule(id);

        const copy = cloneDeep(this.order);
        delete copy.schedule;
        bus.$emit('SET_ORDER', copy);

        Toast.open({
          message: '已刪除例行性訂購資訊',
          type: 'is-success',
        });
      } catch (error) {
        Toast.open({
          message: '目前無法刪除例行性訂購資訊',
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    onConfirm() {
      if (!this.enableSchedule && this.hasSchedule) {
        setTimeout(() => {
          this.openScheduleCloseConfirmModal = true;
        }, 300);
      }
      this.close();
    },
    onScheduleChange(val) {
      this.scheduleChange = val;
      setTimeout(() => {
        this.openScheduleChangeConfirmModal = true;
      }, 300);
    },
    async onScheduleChangeConfirm() {
      if (!this.order || isEmpty(this.scheduleChange)) {
        return;
      }

      try {
        this.isLoading = true;

        const { id } = this.order;
        const schedule = await this.updateSchedule({
          id,
          data: toServerSchedule(this.scheduleChange),
        });

        const copy = cloneDeep(this.order);
        copy.schedule = {
          ...(copy.schedule || {}),
          ...cloneDeep(schedule),
        };
        bus.$emit('SET_ORDER', copy);

        Toast.open({
          message: '已更新例行性訂購資訊',
          type: 'is-success',
        });
      } catch (error) {
        Toast.open({
          message: '目前無法更新例行性訂購資訊',
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
        this.openScheduleChangeConfirmModal = false;
      }
    },
    onScheduleChangeConfirmModalClose() {
      this.scheduleChange = {};
      this.openScheduleChangeConfirmModal = false;
    },
    async onScheduleCloseConfirm() {
      try {
        this.doDeleteSchedule();
      } finally {
        this.openScheduleCloseConfirmModal = false;
      }
    },
    openSettingsModal() {
      this.openScheduleModal = true;
      this.close();
    },
    reset() {
      this.v$.$reset();
      this.enableSchedule = false;
      this.scheduleChange = {};
    },
  },
  validations() {
    return {
      enableSchedule: {},
    };
  },
};
